<template>
  <div class="fs-page">
    <!-- 顶部 -->
    <div class="fs-top">
      <div class="fs-top-l">
        <div class="fs-title">
          <el-button icon="ArrowLeft" text @click="back">返回</el-button>
          <h2>表单数据统计</h2>
          <span class="fs-badge">{{ currentFormName }}</span>
        </div>
        <p class="fs-sub">
          选中具体表单后，下面会多出<b>逐题分布</b>（单选/多选/下拉的选项分布、评分均值、滑块分布）；
          不选则看全部表单的合计。
        </p>
      </div>
      <div class="fs-top-r">
        <el-button icon="Refresh" @click="loadStat">刷 新</el-button>
        <el-button
            v-if="query.formId"
            type="primary"
            icon="Tickets"
            @click="goData">查看数据明细</el-button>
      </div>
    </div>

    <!-- 筛选 -->
    <div class="fs-bar">
      <el-select
          v-model="query.formId"
          clearable
          filterable
          placeholder="选择表单（默认全部）"
          style="width: 260px"
          @change="loadStat">
        <el-option
            v-for="f in formOptions"
            :key="f.id"
            :label="f.title || ('#' + f.id)"
            :value="f.id"></el-option>
      </el-select>
      <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="至"
          start-placeholder="提交开始日"
          end-placeholder="提交结束日"
          @change="loadStat"></el-date-picker>
      <el-button type="primary" icon="Search" @click="loadStat">查 询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重 置</el-button>
    </div>

    <!-- 概览 -->
    <div class="fs-cards">
      <div v-for="c in cards" :key="c.label" class="fs-card">
        <div class="fs-card-ico" :style="{ background: c.bg, color: c.color }">
          <el-icon><component :is="c.icon" /></el-icon>
        </div>
        <div class="fs-card-txt">
          <div class="fs-num">{{ c.value }}<span v-if="c.unit" class="fs-unit">{{ c.unit }}</span></div>
          <div class="fs-label">{{ c.label }}</div>
        </div>
      </div>
    </div>

    <!-- 图表 -->
    <el-row :gutter="14">
      <el-col :span="12">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head"><span class="chart-title">按城市分布</span></div>
          <div ref="cityChart" class="chart-box"></div>
          <div v-if="!loading && !cityList.length" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head"><span class="chart-title">按测试项目分布</span></div>
          <div ref="projectChart" class="chart-box"></div>
          <div v-if="!loading && !projectList.length" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head">
            <span class="chart-title">按学校分布（Top 10）</span>
            <span class="chart-hint">点条形可下钻到该学校的数据明细</span>
          </div>
          <div ref="schoolChart" class="chart-box"></div>
          <div v-if="!loading && !schoolList.length" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head"><span class="chart-title">按提交日期趋势</span></div>
          <div ref="dateChart" class="chart-box"></div>
          <div v-if="!loading && !dateList.length" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head"><span class="chart-title">按处理状态分布</span></div>
          <div ref="statusChart" class="chart-box"></div>
          <div v-if="!loading && !statusList.length" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>

      <!-- 字段级分布：单选/多选/下拉的选项分布、评分均值、滑块分布（选了具体表单才有） -->
      <el-col :span="12" v-for="fd in fieldList" :key="'fd_' + fd.key">
        <div v-loading="loading" class="chart-card">
          <div class="chart-head">
            <span class="chart-title">{{ fd.label || fd.key }}</span>
            <!-- ★ v3.17：条件显示字段的标注 —— 口径由后端保证「不可见不计入分母」，
                 前端只提示；拿不到 schema 就不标（不猜） -->
            <el-tooltip v-if="condKeys[fd.key]" placement="top"
                        content="该字段配了显示条件：不可见的记录本就没有这个值，选项分母按可见记录算">
              <el-tag size="small" type="warning" effect="plain" class="stat-cond-tag">条件</el-tag>
            </el-tooltip>
            <span v-if="fd.avg !== undefined && fd.avg !== null" class="chart-hint">平均 {{ fd.avg }}{{ fd.unit || '' }}（{{ fd.count }} 人）</span>
            <span v-else class="chart-hint">选项分布</span>
          </div>
          <div :ref="'fieldChart_' + fd.key" class="chart-box"></div>
          <!-- ★ 空态 = 「没有说话的数据」：选项类看 options，评分/滑块类没有 options 但有 avg。
               只看 options 会把「平均 X 分（N 人）」的评分图也盖成「暂无数据」，自相矛盾。 -->
          <div v-if="!loading && !(fd.options || []).length && (fd.avg === null || fd.avg === undefined || fd.avg === '')" class="chart-empty">
            <span>这组筛选下暂无数据</span>
            <el-button v-if="hasFilter" text type="primary" size="small" @click="reset">清除筛选</el-button>
            <el-button v-else text type="primary" size="small" @click="back">去表单列表</el-button>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
// 与项目既有图表先例一致：按需引入（整包 echarts 体积过大）
const echarts = require('echarts/lib/echarts');
require('echarts/lib/component/tooltip');
require('echarts/lib/component/grid');
require('echarts/lib/component/legend');
require('echarts/lib/chart/bar');
require('echarts/lib/chart/pie');
require('echarts/lib/chart/line');

import { formPage, formRecordStatistics, formDetail } from '@/api/form';

// 全站唯一主色：焱火绿
const BRAND = '#8dc63f';

// 非可聚合字段：不进「逐题分布」图表（与 image 同类，没有「各选项被选次数」语义）。
//   image / signature → 值是图片 URL（长内容）
//   file              → 值是文件 URL 数组
//   audio             → 值是音频 URL 数组（v3.15 录音；没有任何「选项计数」语义，
//                       排进来只会在图上多出一堆按直链分组的柱子）
//   sort              → 值是有序数组 [{label,value}]，也没有「选项计数」语义
// ⚠️ imageSelect 不在此列 —— 它与 radio 同属选项类，后端按选项聚合后照常出图。
// ⚠️ scanCode 不在此列 —— 值是字符串，同「文本题」走通用兜底（后端也不会把它
//    误判成 choice：类型名不在白名单、schema 里也没有 multiple）。
// ⚠️ richText 不在此列 —— 它是纯展示字段，后端统计在 DISPLAY_TYPES 那一步就跳过了；
//    而且即便后端漏跳，它既不在 choice 白名单、schema 里也没有 multiple，
//    过不了后端「choice || numeric」那道闸，仍然到不了这里。
const NON_CHART_TYPES = ['image', 'signature', 'file', 'audio', 'sort'];

export default {
  name: 'FormStat',
  data() {
    return {
      loading: false,
      formOptions: [],
      query: {
        formId: null,
        startDate: '',
        endDate: '',
      },
      dateRange: [],
      total: 0,
      cityList: [],
      schoolList: [],
      projectList: [],
      dateList: [],
      statusList: [],
      // 浏览量（表单级口径，选中具体表单才有值）与字段级分布
      viewCount: 0,
      fieldList: [],
      // ★ v3.17：配了「显示条件」的字段 key 集合（表头标注用；口径由后端保证，
      //   前端只做提示，不做静默过滤 —— 拿不到可见性就「显示但标注」）。
      condKeys: {},
    };
  },
  computed: {
    /** 是否处于「带筛选」状态 —— 决定空态给「清除筛选」还是「去表单列表」出口 */
    hasFilter() {
      return !!this.query.formId || (Array.isArray(this.dateRange) && this.dateRange.length === 2);
    },
    /** 回收率 = 提交量 / 浏览量（仅当选中具体表单时有意义） */
    recycleRate() {
      if (!this.query.formId || !this.viewCount) return '—';
      const r = (this.total / this.viewCount) * 100;
      return r > 999 ? '999+' : r.toFixed(1);
    },
    /** 顶部口径标：选中表单名 / 全部表单 */
    currentFormName() {
      const f = this.formOptions.find(x => x.id === this.query.formId);
      return f ? (f.title || ('#' + f.id)) : '全部表单';
    },
    /** 概览卡（图标 / 底色 / 单位一次配好，模板只负责循环） */
    cards() {
      const rate = this.recycleRate;
      return [
        { label: '提交总数', value: this.total, icon: 'Tickets', bg: '#eef7e2', color: '#5f9427' },
        { label: '覆盖城市数', value: this.cityList.length, icon: 'MapLocation', bg: '#eaf1fd', color: '#2f6bd8' },
        { label: '覆盖学校数', value: this.schoolList.length, icon: 'School', bg: '#fdf3e6', color: '#e6a23c' },
        { label: '涉及测试项目数', value: this.projectList.length, icon: 'Flag', bg: '#eef7e2', color: '#5f9427' },
        { label: '浏览量', value: this.viewCount, icon: 'View', bg: '#eafaf3', color: '#1f9d6b' },
        { label: '回收率', value: rate, unit: rate === '—' ? '' : '%', icon: 'Odometer', bg: '#fdeceb', color: '#e0754f' },
      ];
    },
  },
  methods: {
    // ---------------------------------------------------------------- 数据
    loadForms() {
      // 只用于下拉选择，取前 200 条足够；真正筛选在后端做
      formPage({ pageIndex: 1, pageSize: 200 }).then(val => {
        if (val && val.code === 200) {
          this.formOptions = (val.data && val.data.records) || [];
        }
      });
    },
    /**
     * 后端返回结构：
     * { total, byCity:[{name,cnt}], bySchool:[...], byProject:[...], byDate:[...] }
     * ⚠️ cnt 来自 MySQL count(*)，可能是字符串/BigDecimal，统一 Number() 转换。
     */
    loadStat() {
      this.loading = true;
      // ★ v3.17：顺带取一次 schema，标记「显示条件字段」（仅用于表头标注，不改统计口径）
      this.loadCondKeys();
      const param = {};
      if (this.query.formId) param.formId = this.query.formId;
      if (this.dateRange && this.dateRange.length === 2) {
        param.startDate = this.dateRange[0];
        param.endDate = this.dateRange[1];
      }
      formRecordStatistics(param).then(val => {
        this.loading = false;
        if (val && val.code === 200 && val.data) {
          const d = val.data;
          this.total = Number(d.total || 0);
          this.cityList = this.normalize(d.byCity);
          this.schoolList = this.normalize(d.bySchool);
          this.projectList = this.normalize(d.byProject);
          this.dateList = this.normalize(d.byDate);
          this.statusList = this.normalizeStatus(d.byStatus);
          this.viewCount = Number(d.viewCount || 0);
          this.fieldList = this.normalizeFields(d.byField);
          this.$nextTick(() => this.renderAll());
        } else {
          this.$message.warning((val && val.message) || '统计获取失败');
        }
      }).catch(() => {
        this.loading = false;
        this.$message.warning('统计获取失败');
      });
    },
    /**
     * ★ v3.17：取 schema，标记配了「显示条件」的字段（仅表头标注，不改统计口径）。
     * 拿不到 schema 就清空标注 —— 宁可不标，也不猜。
     */
    loadCondKeys() {
      const fid = this.query.formId;
      this.condKeys = {};
      if (!fid) return;
      formDetail(fid).then(val => {
        const fields = (val && val.code === 200 && val.data && val.data.schema && Array.isArray(val.data.schema.fields))
            ? val.data.schema.fields : [];
        const map = {};
        fields.forEach(f => {
          if (f && f.key && f.visibleRule && Array.isArray(f.visibleRule.conds) && f.visibleRule.conds.length) {
            map[f.key] = true;
          }
        });
        this.condKeys = map;
      }).catch(() => { this.condKeys = {}; });
    },
    /** 统一成 { name, cnt:Number } 并按 cnt 降序 */
    normalize(list) {
      if (!Array.isArray(list)) return [];
      return list
          .map(it => ({
            name: (it && (it.name === null || it.name === undefined ? '' : String(it.name))) || '未填写',
            cnt: Number((it && it.cnt) || 0),
          }))
          .filter(it => it.name)
          .sort((a, b) => b.cnt - a.cnt);
    },
    reset() {
      this.query.formId = null;
      this.dateRange = [];
      this.loadStat();
    },
    /** 回表单列表（可能从 FormData 或 FormList 进来，统一回列表最省事） */
    back() {
      const p = this.$router.push({ path: '/formList' });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`返回失败：${(err && err.message) || '未知错误'}`));
      }
    },
    /** 看该表单的数据明细（与「按学校分布」下钻落到同一个页面） */
    goData() {
      this.$router.push({ path: '/formData', query: { formId: this.query.formId } });
    },

    /**
     * 处理状态：后端 group by(status) 给的是 0/1/2，
     * 这里翻成中文并按「待处理 → 已处理 → 已作废」固定顺序（否则饼图颜色/顺序会随数量漂移）。
     */
    normalizeStatus(list) {
      const map = { '0': '待处理', '1': '已处理', '2': '已作废' };
      const order = ['待处理', '已处理', '已作废'];
      const hit = {};
      (Array.isArray(list) ? list : []).forEach(it => {
        const key = (it && it.name !== null && it.name !== undefined) ? String(it.name) : '';
        if (key === '') return;
        const label = map[key] || ('状态' + key);
        hit[label] = (hit[label] || 0) + Number((it && it.cnt) || 0);
      });
      return order.filter(k => hit[k] != null).map(k => ({ name: k, cnt: hit[k] }));
    },

    // ---------------------------------------------------------------- 渲染
    chart(key) {
      const refMap = {
        city: this.$refs.cityChart,
        school: this.$refs.schoolChart,
        project: this.$refs.projectChart,
        date: this.$refs.dateChart,
        status: this.$refs.statusChart,
      };
      const el = refMap[key];
      if (!el) return null;
      if (!this._charts[key] || this._charts[key].isDisposed()) {
        this._charts[key] = echarts.init(el);
      }
      return this._charts[key];
    },
    renderAll() {
      this.renderCity();
      this.renderSchool();
      this.renderProject();
      this.renderDate();
      this.renderStatus();
      this.renderFields();
      this._bindDrill();
    },

    /** 字段级分布（单选/多选/下拉选项分布、评分均值、滑块分布） */
    normalizeFields(list) {
      if (!Array.isArray(list)) return [];
      return list
          // 图片/签名/文件/排序题不是可聚合值 → 不进图表（imageSelect 与 radio 同类，保留）
          .filter(it => it && it.key && !NON_CHART_TYPES.includes(it.type))
          .map(it => ({
            key: it.key,
            label: it.label || it.key,
            type: it.type,
            unit: it.unit || '',
            avg: (it.avg === undefined || it.avg === null) ? null : Number(it.avg),
            count: Number(it.count || 0),
            options: (Array.isArray(it.options) ? it.options : []).map(o => ({
              name: String((o && o.name) || '未填'),
              cnt: Number((o && o.cnt) || 0),
            })),
          }));
    },
    renderFields() {
      // 字段图随「选中的表单」变化 → 切换表单时先销毁上一批，避免残留
      Object.keys(this._charts).forEach(k => {
        if (k.indexOf('field_') === 0) {
          try {
            this._charts[k].dispose();
          } catch (e) { /* 忽略 */ }
          delete this._charts[k];
        }
      });
      this.fieldList.forEach(fd => {
        const raw = this.$refs['fieldChart_' + fd.key];
        const el = Array.isArray(raw) ? raw[0] : raw;
        if (!el) return;
        const key = 'field_' + fd.key;
        if (!this._charts[key] || this._charts[key].isDisposed()) {
          this._charts[key] = echarts.init(el);
        }
        const c = this._charts[key];
        // 横向条：reverse 后让数量大的排上面
        const opts = (fd.options || []).slice(0, 10).slice().reverse();
        c.clear();
        c.setOption({
          grid: { left: 100, right: 40, top: 16, bottom: 24 },
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          xAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#606266' } },
          yAxis: {
            type: 'category',
            data: opts.map(o => o.name),
            axisLabel: { color: '#606266', width: 90, overflow: 'truncate' },
          },
          series: [{
            type: 'bar',
            data: opts.map(o => o.cnt),
            barMaxWidth: 18,
            itemStyle: { color: BRAND, borderRadius: [0, 4, 4, 0] },
            label: { show: true, position: 'right', color: '#606266' },
          }],
        });
      });
    },
    /** 学校条形点击 → 下钻到数据页（复用 FormData 已有的 schoolName 模糊筛选） */
    _bindDrill() {
      const c = this.chart('school');
      if (!c || c.__drillBound) return;
      c.__drillBound = true;
      c.on('click', p => {
        // 只认「点中柱子」；点坐标轴 / 空白区域不跳转
        if (!p || p.componentType !== 'series' || !p.name || p.name === '未填写') return;
        this.$router.push({
          path: '/formData',
          query: { formId: this.query.formId || '', schoolName: p.name },
        });
      });
    },
    renderCity() {
      const c = this.chart('city');
      if (!c) return;
      const list = this.cityList.slice(0, 15);
      c.clear();
      c.setOption({
        grid: { left: 40, right: 16, top: 24, bottom: 60 },
        tooltip: { trigger: 'axis' },
        xAxis: {
          type: 'category',
          data: list.map(i => i.name),
          axisLabel: { interval: 0, rotate: list.length > 6 ? 35 : 0, color: '#606266' },
        },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#606266' } },
        series: [{
          type: 'bar',
          data: list.map(i => i.cnt),
          barMaxWidth: 36,
          itemStyle: { color: BRAND, borderRadius: [4, 4, 0, 0] },
          label: { show: true, position: 'top', color: '#606266' },
        }],
      });
    },
    renderSchool() {
      const c = this.chart('school');
      if (!c) return;
      // 横向条：类目轴取反，数值大的排最上
      const list = this.schoolList.slice(0, 10).slice().reverse();
      c.clear();
      c.setOption({
        grid: { left: 90, right: 40, top: 16, bottom: 24 },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#606266' } },
        yAxis: {
          type: 'category',
          data: list.map(i => i.name),
          axisLabel: { color: '#606266', width: 80, overflow: 'truncate' },
        },
        series: [{
          type: 'bar',
          data: list.map(i => i.cnt),
          barMaxWidth: 18,
          cursor: 'pointer',
          itemStyle: { color: '#a5dc5f', borderRadius: [0, 4, 4, 0] },
          label: { show: true, position: 'right', color: '#606266' },
        }],
      });
    },
    renderStatus() {
      const c = this.chart('status');
      if (!c) return;
      const colorMap = { '待处理': '#e6a23c', '已处理': BRAND, '已作废': '#c8c9cc' };
      c.clear();
      c.setOption({
        tooltip: { trigger: 'item', formatter: '{b}<br/>{c} 条 ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [{
          type: 'pie',
          radius: ['38%', '64%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { formatter: '{b}\n{c}' },
          data: this.statusList.map(i => ({
            name: i.name,
            value: i.cnt,
            itemStyle: { color: colorMap[i.name] || BRAND },
          })),
        }],
      });
    },
    renderProject() {
      const c = this.chart('project');
      if (!c) return;
      // project_names 是「、」分隔的多值字段，单条记录可能同时计入多个项目，故此处为「涉及次数」
      const list = this.projectList.slice(0, 8);
      c.clear();
      c.setOption({
        tooltip: { trigger: 'item', formatter: '{b}<br/>涉及 {c} 次 ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [{
          type: 'pie',
          radius: ['38%', '64%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { formatter: '{b}\n{c}' },
          data: list.map(i => ({ name: i.name, value: i.cnt })),
          color: [BRAND, '#a5dc5f', '#7fb52f', '#c3e88d', '#5f9a1f', '#d8efb8', '#4a7a15', '#eaf7da'],
        }],
      });
    },
    renderDate() {
      const c = this.chart('date');
      if (!c) return;
      c.clear();
      c.setOption({
        grid: { left: 40, right: 20, top: 24, bottom: 44 },
        tooltip: { trigger: 'axis' },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.dateList.map(i => i.name),
          axisLabel: { color: '#606266', rotate: this.dateList.length > 10 ? 35 : 0 },
        },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#606266' } },
        series: [{
          type: 'line',
          smooth: true,
          data: this.dateList.map(i => i.cnt),
          symbolSize: 6,
          itemStyle: { color: BRAND },
          lineStyle: { color: BRAND, width: 2 },
          areaStyle: { color: 'rgba(141,198,63,0.18)' },
        }],
      });
    },
    onResize() {
      Object.keys(this._charts).forEach(k => {
        const c = this._charts[k];
        if (c && !c.isDisposed()) c.resize();
      });
    },
  },
  created() {
    // ★ echarts 实例表挂在「组件实例属性」上，不放进 data：
    //   ① 实例对象体量大且内部有循环引用，放进 data 会被 Vue 深度响应式化（纯浪费，还可能报警告）；
    //   ② eslint `vue/no-reserved-keys` 禁止 data 中出现 `_` 开头的 key（会直接导致编译失败）。
    this._charts = {};
  },
  mounted() {
    this.loadForms();
    // 支持从其它页带 ?formId=xxx 直接看某张表单的统计
    const fid = this.$route && this.$route.query ? this.$route.query.formId : null;
    if (fid) this.query.formId = Number(fid);
    this.loadStat();
    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    Object.keys(this._charts).forEach(k => {
      const c = this._charts[k];
      if (c && !c.isDisposed()) c.dispose();
    });
    this._charts = {};
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fs-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.fs-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 顶部 ==================== */
.fs-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fs-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fs-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fs-badge {
  max-width: 260px;
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fs-sub {
  margin: 6px 0 0;
  max-width: 760px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fs-sub b {
  color: var(--yh-t2);
}

.fs-top-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选 ==================== */
.fs-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

/* ==================== 概览卡（图标 + 数字） ==================== */
.fs-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.fs-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: var(--yh-r-lg);
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fs-card-ico {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: var(--yh-r-pill);
  font-size: 19px;
}

.fs-card-txt {
  min-width: 0;
}

.fs-num {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--yh-t1);
}

.fs-unit {
  margin-left: 2px;
  font-size: 13px;
  font-weight: 500;
  color: var(--yh-t3);
}

.fs-label {
  margin-top: 2px;
  font-size: 12px;
  color: var(--yh-t3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ==================== 图表卡 ==================== */
.chart-card {
  position: relative;
  margin-bottom: 14px;
  border-radius: var(--yh-r-lg);
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
  overflow: hidden;
}

.chart-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 12px 16px;
  background: var(--yh-fill);
  border-bottom: 1px solid var(--yh-divide);
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t2);
}

.chart-hint {
  font-size: 12px;
  color: var(--yh-t4);
}

/* ★ v3.17：条件显示字段的标注 */
.stat-cond-tag {
  margin-left: 6px;
}

.chart-box {
  width: 100%;
  height: 300px;
}

.chart-empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 46%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
  font-size: 13px;
  color: var(--yh-t5);
  pointer-events: none;
}

/* 空态里的出口按钮要可点，不能被父层的 pointer-events:none 吃掉 */
.chart-empty .el-button {
  pointer-events: auto;
}
</style>
